import { useEffect, useState } from 'react';
import type { MediaItem, WebVideoProvider } from '@cornerstone/shared';
import { api, type WebVideoValidationResult } from '../client/api';
import { Button, Field } from './ui';
import { cn } from '../utils/cn';
import { backdropCloseProps, useDialogPanel } from './menu';

// =============================================================================
// HBL-151 — WebUrlAddModal
//
// Operator paste a URL → "Validate" → server checks provider/oEmbed/CORS →
// preview pane shows title/thumbnail/duration/warnings → "Add" pushes a draft
// playlist item. Duration may be left blank when the auto-extracted value is
// trusted; for YouTube the player's IFrame API will fill it in at runtime.
// =============================================================================

const PROVIDER_LABEL: Record<WebVideoProvider, string> = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  mp4: '직접 mp4',
};

const ERROR_HINT: Record<
  Extract<WebVideoValidationResult, { ok: false }>['code'],
  string
> = {
  // BRICK-46~48 — 앞 문장은 테스트 설계서 기대 문구, 뒤 문장은 진단 정보.
  // (설계서의 "유효한 미디어에 URL" 은 조사 오타로 보아 바로잡아 적용)
  invalid_url: '형식이 올바르지 않습니다. URL을 다시 확인하세요.',
  http_required: 'HTTPS URL만 허용됩니다. https:// 로 시작하는지 확인하세요.',
  unsupported_provider:
    '유효한 미디어 URL이 아닙니다. YouTube 또는 직접 mp4 URL만 지원합니다.',
  unreachable:
    '유효한 미디어 URL이 아닙니다. 서버에서 URL에 접근하지 못했습니다.',
  forbidden_content_type:
    '유효한 미디어 URL이 아닙니다. video/* Content-Type이 아닙니다.',
  oembed_failed: 'oEmbed 호출이 실패했습니다. 잠시 후 다시 시도하세요.',
  private_or_blocked:
    '비공개/삭제/지역제한 영상일 가능성이 있습니다. 다른 URL을 시도하세요.',
};

export function WebUrlAddModal({
  onAdd,
  onClose,
}: {
  /**
   * Called once the server has finished downloading the URL and registered
   * it as a regular MediaItem. The caller adds a normal `sourceKind:'media'`
   * playlist row so the player applies the full regl warp pipeline from the
   * very first playback — no DOM overlay fallback.
   */
  onAdd: (item: MediaItem) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<
    'idle' | 'validating' | 'validated' | 'downloading' | 'failed'
  >('idle');
  const [result, setResult] = useState<WebVideoValidationResult | null>(null);
  const [durationOverride, setDurationOverride] = useState('');
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{
    percent: number;
    speed: string | null;
    eta: string | null;
  } | null>(null);
  // 0 / null = "no ceiling" (let yt-dlp pick best). 1080 default — balances
  // quality vs. download time for typical projection walls.
  const [maxHeight, setMaxHeight] = useState<number | null>(1080);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Re-typing the URL invalidates any prior validation result so the operator
  // can't accidentally save with stale metadata.
  const updateUrl = (next: string) => {
    setUrl(next);
    if (result || downloadError || progress) {
      setResult(null);
      setStatus('idle');
      setDurationOverride('');
      setDownloadError(null);
      setProgress(null);
    }
  };

  const validate = async () => {
    if (!url.trim()) return;
    setStatus('validating');
    try {
      const r = await api.validateWebVideoUrl(url.trim());
      setResult(r);
      // Auto-fill the duration field with the extracted value so the operator
      // sees what will be used as the hard-timeout fallback. They can still
      // override before saving.
      if (r.ok && r.meta.durationSec != null) {
        setDurationOverride(String(Math.round(r.meta.durationSec)));
      } else {
        setDurationOverride('');
      }
    } catch (err) {
      setResult({
        ok: false,
        code: 'unreachable',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setStatus('validated');
    }
  };

  /**
   * Download the URL synchronously on the server and only commit to the
   * playlist *after* the MediaItem exists. The operator sees a spinner with
   * a clear message during the wait; the playlist never receives a webUrl
   * row, so the player never falls into the un-warped DOM overlay path.
   */
  const add = async () => {
    if (!result || !result.ok || status === 'downloading') return;
    setStatus('downloading');
    setDownloadError(null);
    setProgress({ percent: 0, speed: null, eta: null });
    try {
      const item = await api.cacheWebVideo(url.trim(), {
        maxHeight,
        onProgress: (p) => {
          setProgress({
            percent: p.percent,
            speed: p.speed ?? null,
            eta: p.eta ?? null,
          });
        },
      });
      onAdd(item);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDownloadError(msg);
      setStatus('failed');
    }
  };

  const panel = useDialogPanel<HTMLDivElement>();
  return (
    <div
      data-modal-backdrop="full"
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-8"
      {...backdropCloseProps(onClose)}
    >
      <div
        {...panel}
        role="dialog"
        aria-modal="true"
        aria-label="URL로 추가"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'bg-panel border border-line-strong rounded-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] outline-none',
          'w-[672px] max-w-full max-h-[min(720px,100vh-80px)] flex flex-col overflow-hidden',
        )}
      >
        <header className="flex-none flex items-center justify-between px-5 py-4 border-b border-line bg-panel-2">
          <span className="font-display text-[19px] font-semibold">
            URL로 추가
          </span>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[14px] text-text-dim hover:text-text"
            title="닫기 (Esc)" aria-label="닫기" data-modal-close
          >
            ✕
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto p-5 flex flex-col gap-4">
          <p className="rounded-md border border-line bg-panel-2 px-3 py-2 text-[13px] text-text-dim" role="note">
            <span className="font-medium text-text">YouTube</span> 링크를 지원합니다. 영상 페이지 주소를 그대로 붙여 넣으세요.
          </p>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Field
                label="URL"
                value={url}
                placeholder="https://www.youtube.com/watch?v=…"
                onChange={(e) => updateUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && url.trim()) {
                    e.preventDefault();
                    validate();
                  }
                }}
              />
            </div>
            <Button
              variant="default"
              onClick={validate}
              disabled={!url.trim() || status === 'validating'}
            >
              {status === 'validating' ? '검증 중…' : '검증'}
            </Button>
          </div>

          {result && result.ok && (
            <div className="flex gap-3 p-3 border border-line rounded bg-panel-2">
              {result.meta.thumbnailUrl && (
                <img
                  src={result.meta.thumbnailUrl}
                  alt=""
                  className="w-32 h-20 shrink-0 object-cover rounded border border-line"
                />
              )}
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[12px] uppercase tracking-monoWide px-1.5 py-0.5 rounded-sm bg-accent/20 text-accent border border-accent/40">
                    {PROVIDER_LABEL[result.provider]}
                  </span>
                  {result.warnings.length > 0 && (
                    <span className="font-mono text-[12px] text-orange-300">
                      ⚠ {result.warnings.length}
                    </span>
                  )}
                </div>
                <div className="text-[16px] text-text font-medium line-clamp-2 break-words">
                  {result.meta.title ?? '(제목 없음)'}
                </div>
                <div className="font-mono text-[12px] text-text-dim">
                  {result.meta.durationSec
                    ? `재생 시간: ${Math.round(result.meta.durationSec)}초 (자동)`
                    : '재생 시간: 알 수 없음 — 아래에서 최대 재생 시간을 설정하세요'}
                </div>
                {result.warnings.map((w, i) => (
                  <div
                    key={i}
                    className="font-mono text-[12px] text-orange-300"
                  >
                    • {w}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result && !result.ok && (
            <div className="p-3 border border-red/40 rounded bg-red/10">
              <div className="font-mono text-[12px] uppercase tracking-monoWide text-red mb-1">
                검증 실패
              </div>
              <div className="text-[14px] text-text">{result.message}</div>
              <div className="font-mono text-[12px] text-text-dim mt-1">
                {ERROR_HINT[result.code]}
              </div>
            </div>
          )}

          {result && result.ok && (
            <div className="flex flex-col gap-1.5">
              <span className="tk-tag">다운로드 해상도</span>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { v: 480, label: '480p' },
                  { v: 720, label: '720p' },
                  { v: 1080, label: '1080p' },
                  { v: 1440, label: '1440p' },
                  { v: 2160, label: '4K' },
                  { v: null, label: '최고 화질' },
                ].map((opt) => {
                  const active = maxHeight === opt.v;
                  return (
                    <button
                      key={opt.v ?? 'best'}
                      type="button"
                      onClick={() => setMaxHeight(opt.v)}
                      disabled={status === 'downloading'}
                      className={cn(
                        'px-3 py-1 rounded border font-mono text-[13px] transition-colors',
                        active
                          ? 'bg-accent border-accent text-[#1a0f00] font-semibold'
                          : 'border-line text-text-dim hover:text-text hover:border-line-strong',
                        status === 'downloading' && 'opacity-40 cursor-not-allowed',
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <span className="font-mono text-[12px] text-text-dimmer">
                높을수록 화질 좋음 + 다운로드 시간↑. 워프 매핑 픽셀 해상도보다 같거나 높게 권장.
              </span>
            </div>
          )}

          {result && result.ok && (
            <Field
              label="재생 시간 지정 (초, 선택)"
              type="number"
              min={1}
              value={durationOverride}
              placeholder="자동"
              onChange={(e) => setDurationOverride(e.target.value)}
            />
          )}

          {status === 'downloading' && (
            <div className="p-3 border border-line rounded bg-panel-2">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[12px] uppercase tracking-monoWide text-text-dim">
                  다운로드 중…
                </span>
                <span className="font-mono text-[13px] tabular-nums text-text">
                  {progress ? progress.percent.toFixed(1) : '0.0'}%
                </span>
              </div>
              <div className="h-2 bg-panel border border-line rounded-sm overflow-hidden">
                <div
                  className="h-full bg-accent transition-[width] duration-150 ease-linear"
                  style={{
                    width: `${Math.max(0, Math.min(100, progress?.percent ?? 0))}%`,
                  }}
                />
              </div>
              <div className="mt-2 flex items-center justify-between font-mono text-[12px] text-text-dim">
                <span>
                  {progress?.speed ? `속도 ${progress.speed}` : '속도 측정 중…'}
                </span>
                <span>
                  {progress?.eta ? `남은 시간 ${progress.eta}` : '남은 시간 산정 중…'}
                </span>
              </div>
              <div className="mt-2 text-[13px] text-text-dim">
                서버가 yt-dlp로 영상을 다운로드해 미디어 라이브러리에 등록 중입니다.
                영상 길이·네트워크 상태에 따라 수십 초 ~ 수 분 소요됩니다.
              </div>
            </div>
          )}

          {status === 'failed' && downloadError && (
            <div className="p-3 border border-red/40 rounded bg-red/10">
              <div className="font-mono text-[12px] uppercase tracking-monoWide text-red mb-1">
                다운로드 실패
              </div>
              <div className="text-[14px] text-text">{downloadError}</div>
              <div className="font-mono text-[12px] text-text-dim mt-1">
                서버에 yt-dlp가 설치되어 있는지, URL이 다운로드 가능한 영상인지 확인하세요.
              </div>
            </div>
          )}
        </div>
        <footer className="flex-none flex justify-end gap-2 px-5 py-3 border-t border-line bg-panel-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={status === 'downloading'}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={add}
            disabled={!result || !result.ok || status === 'downloading'}
          >
            {status === 'downloading' ? '다운로드 중…' : '추가'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
